"use client";

import { ColourVariant } from "@shared/ui.design-system";
import { useRouter } from "@shared/utils.routing";
import isAbsoluteUrl from "is-absolute-url";
import type { LinkProps as NextLinkProps } from "next/link";
import NextLink from "next/link";
import { useParams } from "next/navigation";
import type { PropsWithChildren } from "react";
import React, { forwardRef } from "react";

import { Typography } from "../Typography/Typography";
import { TextVariants } from "../Typography/Typography.types";

import {
  contentWrapperStyles,
  iconWrapperStyles,
  linkStyles
} from "./Link.css";

export type LinkVariantType = "primary" | "secondary";
export type LinkSize = "small" | "medium" | "large" | "none";
export type LinkWidth = "default" | "minContent" | "full";

/**
 * Defines the props for the Link component.
 */
export type LinkProps = Omit<NextLinkProps, "href"> & {
  /**
   * The URL to navigate to.
   */
  href: string;
  /**
   * The visual style of the link (only applies when using text prop).
   * @default 'link'
   */
  variant?: LinkVariantType;
  /**
   * The text content displayed inside the link.
   * If provided, uses styled Typography. If not, renders children as-is.
   */
  text?: string;
  /**
   * The size of the link, affecting font size (only applies when using text prop).
   * @default 'medium'
   */
  size?: LinkSize;
  /**
   * The horizontal alignment of the link's content (only applies when using text prop).
   * @default 'left'
   */
  textAlign?: "left" | "center" | "right";
  /**
   * If `true`, disables the link and prevents navigation.
   * @default false
   */
  disabled?: boolean;
  /**
   * A descriptive label for accessibility.
   */
  ariaLabel?: string;
  /**
   * The target window for the link.
   * @default '_self'
   */
  target?: "_self" | "_blank" | "_parent" | "_top";
  /**
   * Whether to use the default locale in the URL.
   * @default true
   */
  useDefaultLocale?: boolean;
  /**
   * A unique identifier for testing purposes.
   */
  dataTestId?: string;
  /**
   * Defines the width behavior of the link.
   * 'default': an intrinsic width based on content and padding.
   * 'minContent': shrinks to the minimum possible width.
   * 'full': expands to the full width of its container.
   * @default 'default'
   */
  width?: LinkWidth;
  /**
   * Callback function to be executed when the link is clicked.
   */
  onClick?: (e?: React.MouseEvent<HTMLAnchorElement>) => void;
  /**
   * If `true`, removes all padding from the link regardless of size.
   * @default false
   */
  noPadding?: boolean;
  /**
   * An icon element to display to the left of the text (only applies when using text prop).
   */
  leftIcon?: React.ReactNode;
  /**
   * An icon element to display to the right of the text (only applies when using text prop).
   */
  rightIcon?: React.ReactNode;
  /**
   * If provided, applies the specified colour to the link text. This should not have a default as to not override the variant styles.
   */
  textColour?: ColourVariant;
} & PropsWithChildren;

const getTypographyColour = (variant: LinkVariantType): ColourVariant => {
  return variant === "primary" ? "primary-dark" : "secondary-soft";
};

const getFontSize = (size: LinkSize): TextVariants => {
  if (size === "small") return "link-xs";
  return "link-md";
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      href: rawHref,
      text,
      variant = "primary",
      width = "default",
      size = "medium",
      textAlign = "left",
      disabled = false,
      noPadding = false,
      ariaLabel,
      target = "_self",
      useDefaultLocale = true,
      dataTestId = "Link",
      onClick,
      leftIcon,
      rightIcon,
      children,
      textColour,
      ...props
    },
    ref
  ) => {
    const params = useParams();
    const store = params?.store ?? "en";
    const router = useRouter();

    let href = rawHref;

    // Add locale to relative URLs if needed
    if (router && useDefaultLocale) {
      if (!isAbsoluteUrl(rawHref) && store !== "en") {
        href = rawHref.replace(/^\/?/, `/${store}/`);
      }
    }

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      onClick?.(e);
    };

    // If text prop is provided, render styled version
    if (text) {
      const typographyColour = textColour || getTypographyColour(variant);
      const fontSize = getFontSize(size);

      return (
        <NextLink
          {...props}
          ref={ref}
          href={href}
          target={target}
          rel={target === "_blank" ? "noopener noreferrer" : undefined}
          aria-label={ariaLabel || text}
          aria-disabled={disabled}
          data-testid={dataTestId}
          onClick={handleClick}
          className={`${linkStyles({ size, width, textAlign, noPadding })}`}
        >
          <div className={contentWrapperStyles}>
            {leftIcon && <span className={iconWrapperStyles}>{leftIcon}</span>}

            <Typography
              textAlign={textAlign}
              as="span"
              variant={fontSize}
              colour={typographyColour}
            >
              {text}
            </Typography>

            {rightIcon && (
              <span className={iconWrapperStyles}>{rightIcon}</span>
            )}
          </div>
        </NextLink>
      );
    }

    // If children are provided, render unstyled wrapper
    return (
      <NextLink
        {...props}
        ref={ref}
        href={href}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : undefined}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        data-testid={dataTestId}
        onClick={handleClick}
        className={`${linkStyles({ size, width, textAlign, noPadding })}`}
      >
        {children}
      </NextLink>
    );
  }
);

Link.displayName = "Link";
